import { parse, format, startOfDay, differenceInMinutes, isValid } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { t, TFunction } from 'i18next';

import { broadcastsEndpoints } from '@/api/endpoints';
import {
  Broadcast,
  BroadcastActionComponentType,
  BroadcastComponentParameter,
  BroadcastStatus,
  Subscriber,
} from '@/shared/types/broadcast';
import { debouncedCheckNameAvailability } from '@/util/debouncedCheckNames';
import { LENGTH_XS } from '@/util/validation/constants';
import { emailPattern } from '@/util/validation/patterns';

import { userContextVariables } from '../tryIt/components/VariablesSection/constants';

export const SEND_NOW = 'now';

export const broadcastRules = (currentName?: string) => ({
  name: {
    required: true,
    maxLength: LENGTH_XS,
    validate: {
      uniqueName: async (value: string) => {
        if (!value?.trim()) {
          return true;
        }

        // Skip validation if the name hasn't changed
        if (currentName && value.trim().toLowerCase() === currentName.toLowerCase()) {
          return true;
        }

        try {
          const endpoint = broadcastsEndpoints.checkName(value.trim(), {});
          const { available } = await debouncedCheckNameAvailability(endpoint);
          return available || t('validation.name_already_exists');
        } catch (error) {
          return error?.message || t('validation.error_checking_name');
        }
      },
    },
  },
  channel: { required: true },
});

export const addPlusSignToPhone = (phone: string) => {
  if (phone[0] !== '+') {
    return `+${phone}`;
  }
  return phone;
};

export const extractContentFromContextVariable = (str: string) => {
  if (!str) {
    return null;
  }
  const regex = /\{\{\$(.*?)\}\}/;
  const match = str.match(regex);
  return match ? match[1] : null;
};

/**
 * Formats options for autocomplete input field based on the provided parameter and component type.
 *
 * @param {BroadcastComponentParameter} parameter - The parameter to format into an option, which may contain text or media attributes.
 * @param {BroadcastActionComponentType} type - The type of the component, used to determine specific formatting logic.
 * @returns {Object | null} - Returns an object representing the option with `value` and `label` keys, or `null` if conditions are not met.
 **/

export const formatOptionsFromComponent = (
  parameter: BroadcastComponentParameter,
  type: BroadcastActionComponentType
) => {
  if (!parameter?.text && type === 'body') {
    return null;
  }

  const { id, link } = parameter?.image || parameter?.video || parameter?.document || {};

  const value = parameter?.text || id || link;

  const textWithoutBrackets = extractContentFromContextVariable(value);
  if (textWithoutBrackets) {
    return { value, label: textWithoutBrackets };
  }
  if (value) {
    return { value, label: value };
  }

  return null;
};

export const getScheduleTime = (day: string, hour: string): number | string => {
  if (day === SEND_NOW) {
    return SEND_NOW;
  }

  if (day && hour) {
    // Parse the date and time in local timezone
    const localDateTime = parse(`${day} ${hour}`, 'yyyy-MM-dd h:mm a', new Date());
    if (!isValid(localDateTime)) {
      console.error('[getScheduleTime] Invalid date/time, using current time', { day, hour });
      return new Date().getTime();
    }
    return localDateTime.getTime();
  } else if (day) {
    // Parse just the date and get start of day in local timezone
    const localDate = parse(day, 'yyyy-MM-dd', new Date());
    const startOfLocalDay = startOfDay(localDate);
    return startOfLocalDay.getTime();
  } else if (hour) {
    const today = format(new Date(), 'yyyy-MM-dd');
    const localDateTime = parse(`${today} ${hour}`, 'yyyy-MM-dd h:mm a', new Date());
    if (!isValid(localDateTime)) {
      console.error('[getScheduleTime] Invalid time, using current time', { hour });
      return new Date().getTime();
    }
    return localDateTime.getTime();
  }

  return new Date().getTime();
};

const allowedUserFields = userContextVariables.map((variable) => variable.value);

/**
 * Sets a nested property in an object given an array of keys.
 *
 * @param {Object} obj - The object in which to set the property.
 * @param {string[]} keys - An array of strings representing the keys, in order, for the nested property.
 * @param {*} value - The value to set at the specified nested property.
 */
export const setNestedProperty = (obj, keys, value) => {
  keys.reduce((acc, key, index) => {
    if (index === keys.length - 1) {
      acc[key] = value;
    } else {
      acc[key] = acc[key] || {};
    }
    return acc[key];
  }, obj);
};

/**
 * Formats file context data by converting rows of data into a structured object format.
 *
 * @param {string[]} firstRow - An array of strings representing the headers/keys for each column in the data.
 * @param {string[][]} data - A 2D array where each sub-array represents a row of data.
 * @returns {Object} An object containing errorCounter and an array of fileSubscribers.
 */
export const formatFileContext = (
  firstRow: string[],
  data: string[][]
): { fileSubscribers?: Partial<Subscriber>[]; invalidFile?: boolean } => {
  const fileSubscribers: Partial<Subscriber>[] = [];

  for (let i = 1; i < data.length; i++) {
    const row = data[i];
    let hasEmptyValue = false;
    let hasInvalidEmail = false;
    //TODO can we detect if there is a column named id , to use that ? Other wise use the first
    const id = row[0]?.trim();
    const formattedRow = {
      external_id: id ? addPlusSignToPhone(id) : '',
      metadata: { source: 'file' as const },
      context: { user: {} },
    };

    for (let j = 1; j < firstRow.length; j++) {
      const key = firstRow[j]?.trim();
      const value = row[j]?.trim();
      const keys = key.split('.');

      if (
        key === 'user' ||
        key === 'global' ||
        firstRow.some((k, i) => keys.length === 1 && i !== j && k.split('.')[0] === keys[0])
      ) {
        return { invalidFile: true };
      }

      // Allow user.key if it belongs to user context
      if (allowedUserFields.includes(key)) {
        if (key === 'user.email' && !emailPattern.test(value)) {
          hasInvalidEmail = true;
        }
        formattedRow.context.user[keys[1]] = value;
        //Save user.key in root context if it doesn't belong to user context
      } else if (key.includes('user.')) {
        formattedRow.context[keys[1]] = value;
      } else {
        setNestedProperty(formattedRow.context, keys, value);
      }
      if (!value) {
        hasEmptyValue = true;
      }
    }

    // Remove the user object if it is empty
    if (Object.keys(formattedRow.context.user).length === 0) {
      delete formattedRow.context.user;
    }
    if (fileSubscribers.find((s) => s.external_id === formattedRow.external_id)) {
      continue;
    }
    if (hasEmptyValue) {
      fileSubscribers.push({ ...formattedRow, status_code: 'MISSING_VARIABLE' });
    } else if (hasInvalidEmail) {
      fileSubscribers.push({ ...formattedRow, status_code: 'INVALID_EMAIL' });
    } else {
      fileSubscribers.push(formattedRow);
    }
  }

  return { fileSubscribers };
};
const TWO_MINUTES = 2;
export const isScheduledToBeSentNow = (broadcast: Partial<Broadcast>) => {
  if (!broadcast.broadcast_id || !broadcast?.scheduled_at) {
    return false;
  }

  if (broadcast.status === 'draft') {
    return false;
  }

  const now = new Date();
  const date = new Date(broadcast.scheduled_at);

  return Math.abs(differenceInMinutes(now, date)) <= TWO_MINUTES;
};

export const calculatePercentage = (numerator, denominator) => {
  if (!denominator || !numerator) {
    return 0;
  }
  return ((numerator / denominator) * 100).toFixed(0);
};

export const showBroadcastBanner = (
  status: BroadcastStatus,
  broadcast: Partial<Broadcast>,
  t: TFunction
) => {
  if (status === 'ready' && broadcast?.scheduled_at) {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const scheduledAtUtcDate = new Date(broadcast.scheduled_at);
    const scheduledAtLocalDate = toZonedTime(scheduledAtUtcDate, timezone);
    const formattedDate = format(scheduledAtLocalDate, 'yyyy-MM-dd h:mma').toLowerCase();

    return {
      show: true,
      bannerVariant: 'info',
      bannerTitle: t('broadcasts.banner_scheduled', { 0: formattedDate }),
      bannerSubstitle: t('broadcasts.banner_scheduled_body'),
    };
  }
  if (status === 'sent') {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const updatedAtUtcDate = new Date(broadcast.updated);
    const updatedAtLocalDate = toZonedTime(updatedAtUtcDate, timezone);
    const formattedDate = format(updatedAtLocalDate, 'yyyy-MM-dd h:mma').toLowerCase();

    return {
      show: true,
      bannerVariant: 'success',
      bannerTitle: t('broadcasts.banner_sent', { 0: formattedDate }),
    };
  }
  if (status === 'in_progress') {
    return { show: true, bannerVariant: 'info', bannerTitle: t('broadcasts.banner_in_progress') };
  }
  return { show: false };
};

export const flattenKeys = (obj, prefix = '') =>
  Object.entries(obj).reduce((acc, [key, value]) => {
    const prefixedKey = prefix ? `${prefix}.${key}` : key;
    if (typeof value === 'object' && value !== null) {
      acc.push(...flattenKeys(value, prefixedKey));
    } else {
      acc.push(prefixedKey);
    }
    return acc;
  }, []);

export const getContextVariableOptions = (subscribers: Partial<Subscriber>[]) => {
  if (!subscribers || subscribers.length === 0) {
    return [];
  }
  const allKeys = Array.from(
    new Set(subscribers.flatMap((subscriber) => flattenKeys(subscriber.context)))
  );
  const options = allKeys.map((key) => ({
    label: key,
    value: `{{$${key}}}`,
    type: 'context_variable',
  }));
  return options;
};
